/**
 * The toast one side of the toggle raises (R48) — the type, and the two sentences it carries.
 *
 * It sits **inside the surface that fires it**: the enable toast under ② Modal de ativar, the
 * disable one under ③ Modal de desativar. Not in a block of its own at the foot of the form —
 * the toast is the last frame of that modal's story, and whoever is writing the confirmation copy
 * is the person who should see what the screen says a second later.
 *
 * **The type is the switch, and it is a single select with a "Nenhum" option** — not a switch plus
 * a select. Two controls for one decision produce "on, with no type", which has no drawing; the
 * contract spells this out (`03-contratos.md`, *Painel — o toast do toggle*).
 *
 * The texts stay on screen and stay stored with the type on "Nenhum". That is the whole point of
 * the type being the switch: turning a toast off and on again costs one select, not retyping three
 * languages. The block says so, because a field that goes quiet looks like a field about to be
 * wiped.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { css } from '@emotion/css';
import { useToast } from '@group-link-one/gl-components';
import { UTButton, UTTextArea, UTTextField } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { OptInToastType } from '../../../Types/OptInFeatures.types';
import type {
  OptInTextsDraft,
  OptInToastSide,
  OptInToastsDraft,
} from '../../../Utils/optInFeatureForm';
import { OPT_IN_TOAST_CHOICES } from '../../../Utils/optInFeatureForm';

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
  padding: var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
`;

const headStyle = css`
  display: flex;
  align-items: baseline;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const headTitleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const headHintStyle = css`
  margin: 0;
  flex: 1 1 320px;
  min-width: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const gridStyle = css`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: var(--space-s) var(--space-m);

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

/** The JSON key, in mono, exactly as the text fields above label themselves. */
const monoLabelStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const fieldNameStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const selectStyle = css`
  height: 36px;
  padding: 0 var(--space-s);
  border-radius: var(--radius-control);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 1px;
  }
`;

/** What the block says while the toast is off — the sentence that stops someone clearing the texts. */
const noteStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const warningNoteStyle = css`
  ${noteStyle};
  color: var(--gl-yellow-dark);
`;

/**
 * The sentinel the `<select>` uses for `null`.
 *
 * An `<option>` value is always a string, and `''` would be indistinguishable from a type somebody
 * had not chosen yet — which is precisely the state the contract says must not exist.
 */
const NONE = '__none__';

const typeToOption = (type: OptInToastType | null): string => type ?? NONE;

const optionToType = (value: string): OptInToastType | null => {
  const choice = OPT_IN_TOAST_CHOICES.find(entry => typeToOption(entry.value) === value);

  return choice ? choice.value : null;
};

export interface OptInToastFieldsProps {
  side: OptInToastSide;
  toasts: OptInToastsDraft;
  /** The active language's slice — the toast sentences are translated like the other texts. */
  texts: OptInTextsDraft;
  onToastChange: <K extends keyof OptInToastsDraft>(field: K, value: OptInToastsDraft[K]) => void;
  onTextChange: (field: keyof OptInTextsDraft, value: string) => void;
}

export const OptInToastFields = ({
  side,
  toasts,
  texts,
  onToastChange,
  onTextChange,
}: OptInToastFieldsProps): JSX.Element => {
  const { addToast } = useToast();
  const type = toasts[side.typeField];
  const title = texts[side.titleField];
  const body = texts[side.bodyField];
  const isMissingTitle = type !== null && title.trim() === '';

  /*
    The preview is the real `useToast` of `gl-components` — the same call `useOptInFeatureModals`
    will make, with the same four types. Drawing a lookalike here would agree with the dashboard
    everywhere except where it matters: how long a title fits before it wraps.
  */
  const preview = (): void => {
    if (type === null) return;

    addToast({
      type: type.toLowerCase() as 'info' | 'warning' | 'success' | 'error',
      title: title.trim() === '' ? '(sem título)' : title,
      ...(body.trim() === '' ? {} : { message: body }),
    });
  };

  return (
    <div id={`opt-in-feature-toast-${side.id}`} className={wrapStyle}>
      <div className={headStyle}>
        <span className={headTitleStyle}>{side.title}</span>
        <p className={headHintStyle}>{side.hint}</p>
        {type === null ? null : (
          <UTButton
            fitContent
            variant='secondary'
            size='small'
            id={`opt-in-feature-toast-${side.id}-preview`}
            text='Pré-visualizar'
            icon='Misc.Eye'
            onClick={preview}
          />
        )}
      </div>

      <div className={gridStyle}>
        <div className={fieldStyle} style={{ gridColumn: 'span 4' }}>
          <span className={monoLabelStyle}>
            {side.typeField} <span className={fieldNameStyle}>· Tipo</span>
          </span>
          <select
            id={`opt-in-feature-toast-${side.id}-type`}
            className={selectStyle}
            aria-label={side.title}
            value={typeToOption(type)}
            onChange={event => {
              onToastChange(side.typeField, optionToType(event.target.value));
            }}
          >
            {OPT_IN_TOAST_CHOICES.map(choice => (
              <option key={typeToOption(choice.value)} value={typeToOption(choice.value)}>
                {choice.label}
              </option>
            ))}
          </select>
        </div>

        <div className={fieldStyle} style={{ gridColumn: '5 / span 8' }}>
          <span className={monoLabelStyle}>
            {side.titleField} <span className={fieldNameStyle}>· Título</span>
          </span>
          <UTTextField
            id={`opt-in-feature-text-${side.titleField}`}
            value={title}
            state={isMissingTitle ? 'error' : 'normal'}
            onChange={event => {
              onTextChange(side.titleField, event.target.value);
            }}
          />
        </div>

        <div className={fieldStyle} style={{ gridColumn: '1 / -1' }}>
          <span className={monoLabelStyle}>
            {side.bodyField} <span className={fieldNameStyle}>· Descrição (opcional)</span>
          </span>
          <UTTextArea
            id={`opt-in-feature-text-${side.bodyField}`}
            textAreaSize='small'
            value={body}
            onChange={event => {
              onTextChange(side.bodyField, event.target.value);
            }}
          />
        </div>
      </div>

      {isMissingTitle ? (
        <span id={`opt-in-feature-toast-${side.id}-missing`} className={warningNoteStyle}>
          Com um tipo escolhido e o título vazio, o toast aparece como uma caixa vazia na tela de{' '}
          {side.audience} — e o BFF recusa o cadastro. Escreva o título, ou volte o tipo para
          «Nenhum».
        </span>
      ) : (
        <span className={noteStyle}>
          {type === null
            ? 'Em «Nenhum» este toast não dispara — e os textos acima continuam guardados, prontos para quando você ligar de novo. Desligar aqui não apaga nada.'
            : 'O título é obrigatório com o tipo escolhido; a descrição é opcional, e um toast só com título é legítimo.'}
        </span>
      )}
    </div>
  );
};
